"use client";

import React, { useState, useMemo } from "react";
import { diffLines, diffWordsWithSpace, createTwoFilesPatch } from "diff";
import { Prism } from "prism-react-renderer";
import { CodeHighlight, normalizeLanguage } from "@/components/CodeHighlight";
import {
  Wand2,
  ArrowLeftRight,
  Columns2,
  AlignJustify,
  BookOpen,
  Copy,
  Check,
  Download,
  Sparkles,
  FileCode2,
  Upload,
  Trash2,
  ChevronsUpDown,
  PenLine,
} from "lucide-react";

type DetectedKind = "json" | "markdown" | "typescript" | "css" | "markup" | "text";
type ViewMode = "split" | "unified" | "markdown";

const KIND_LABELS: Record<DetectedKind, string> = {
  json: "JSON",
  markdown: "Markdown",
  typescript: "TypeScript / JS",
  css: "CSS",
  markup: "HTML / XML",
  text: "Klartext / Vertrag",
};

const PRESETS: Array<{
  id: string;
  label: string;
  left: string;
  right: string;
  defaultView?: ViewMode;
}> = [
  {
    id: "markdown",
    label: "Markdown + Tabelle",
    defaultView: "split",
    left: `## Release-Planung v2.4

Die Auslieferung erfolgt **schrittweise** über das EU-Rechenzentrum in Frankfurt.

| Dienst | Timeout | Offline-Cache |
| :--- | :--- | :--- |
| Suche-API | 1500 ms | Nein |
| Sync-Worker | 4000 ms | Aktiv |

- [x] Sicherheits-Audit abgeschlossen
- [ ] Lasttest mit 500 Anfragen/Sekunde

\`\`\`typescript
export const config = { retries: 2, region: "eu-central-1" };
\`\`\``,
    right: `## Release-Planung v2.5

Die Auslieferung erfolgt **vollautomatisch** über die EU-Rechenzentren in Frankfurt und Wien.

| Dienst | Timeout | Offline-Cache |
| :--- | :--- | :--- |
| Suche-API | 850 ms | Aktiv (IndexedDB) |
| Sync-Worker | 4000 ms | Aktiv |
| Telemetrie | 1200 ms | Nein |

- [x] Sicherheits-Audit abgeschlossen
- [x] Lasttest mit 1.200 Anfragen/Sekunde

\`\`\`typescript
export const config = { retries: 3, region: "eu-central-1", fallback: "eu-south-2" };
\`\`\``,
  },
  {
    id: "json",
    label: "Minifiziertes JSON",
    defaultView: "split",
    left: `{"region":"eu-central","features":{"offline":true,"maxUploadMb":10,"formats":["svg","png"]},"timeoutMs":3000,"version":1}`,
    right: `{"version":2,"timeoutMs":3000,"region":"eu-central","features":{"formats":["svg","png","webp"],"maxUploadMb":25,"offline":true}}`,
  },
  {
    id: "typescript",
    label: "TypeScript-Code",
    defaultView: "split",
    left: `export async function fetchUserProfile(userId: string) {
  const res = await fetch(\`/api/users/\${userId}\`);
  if (!res.ok) {
    throw new Error("Load failed");
  }
  return res.json();
}`,
    right: `export async function fetchUserProfile(userId: string, timeoutMs = 4500) {
  const res = await fetch(\`/api/users/\${encodeURIComponent(userId)}\`, {
    signal: AbortSignal.timeout(timeoutMs),
  });
  if (!res.ok) {
    throw new Error(\`HTTP \${res.status}: Profil nicht erreichbar\`);
  }
  return res.json();
}`,
  },
  {
    id: "contract",
    label: "Vertrag / AGB",
    defaultView: "split",
    left: `§ 4 Nutzungsrechte und Kündigungsfrist
Die Auftraggeberin erhält ein zeitlich unbegrenztes, einfaches Nutzungsrecht an den erstellten Arbeitsergebnissen nach vollständiger Vergütung.
Die Zahlungsfrist beträgt 14 Kalendertage ab Rechnungsdatum ohne Abzug.
Eine ordentliche Kündigung der Wartungsvereinbarung ist mit einer Frist von 4 Wochen zum Monatsende in Textform (E-Mail genügt) möglich.`,
    right: `§ 4 Nutzungsrechte und Kündigungsfrist
Die Auftraggeberin erhält ein auf 24 Monate befristetes, einfaches Nutzungsrecht an den erstellten Arbeitsergebnissen nach vollständiger Vergütung.
Die Zahlungsfrist beträgt 30 Kalendertage ab Rechnungsdatum ohne Abzug.
Eine ordentliche Kündigung der Wartungsvereinbarung ist mit einer Frist von 3 Monaten zum Quartalsende schriftlich möglich.`,
  },
];

function detectContentKind(a: string, b: string): DetectedKind {
  const combined = `${a.trim()}\n${b.trim()}`.trim();
  if (!combined) return "text";

  const trimA = a.trim();
  const trimB = b.trim();
  const looksJson =
    (trimA.startsWith("{") || trimA.startsWith("[")) &&
    (trimB.startsWith("{") || trimB.startsWith("["));
  if (looksJson) {
    try {
      if (trimA) JSON.parse(trimA);
      if (trimB) JSON.parse(trimB);
      return "json";
    } catch {
      if (/"[^"]+"\s*:/.test(combined)) return "json";
    }
  }

  if (
    /^#{1,4}\s+\S/m.test(combined) ||
    /^\|.+\|\s*\r?\n\|[\s:|-]+\|/m.test(combined) ||
    /^-\s\[[ xX]\]\s/m.test(combined) ||
    /^```[\w-]*/m.test(combined)
  ) {
    return "markdown";
  }

  if (/<(!DOCTYPE|html|div|section|svg|meta|head|body)\b/i.test(combined)) {
    return "markup";
  }

  if (
    /\b(import|export|interface|function|const|let|return|async|await)\b/.test(combined) &&
    /[{};()=>]/.test(combined)
  ) {
    return "typescript";
  }

  if (/@media\b|\.[a-zA-Z0-9_-]+\s*\{[^}]*:[^}]*;/m.test(combined)) {
    return "css";
  }

  return "text";
}

function sortJsonDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortJsonDeep);
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).sort(([k1], [k2]) =>
      k1.localeCompare(k2)
    );
    return Object.fromEntries(entries.map(([k, v]) => [k, sortJsonDeep(v)]));
  }
  return value;
}

function smartFormatText(raw: string, kind: DetectedKind, sortKeys: boolean): { text: string; error?: string } {
  const normalizedEol = raw.replace(/\r\n/g, "\n");
  if (!normalizedEol.trim()) return { text: "" };

  if (kind === "json") {
    try {
      const parsed = JSON.parse(normalizedEol);
      const ready = sortKeys ? sortJsonDeep(parsed) : parsed;
      return { text: JSON.stringify(ready, null, 2) };
    } catch (err) {
      return {
        text: normalizedEol,
        error: err instanceof Error ? `JSON-Syntaxfehler: ${err.message}` : "Ungültiges JSON",
      };
    }
  }

  if (kind === "typescript" || kind === "css") {
    let working = normalizedEol.replace(/\t/g, "  ");
    if (working.split("\n").length <= 2 && working.length > 90 && /[{;}]/.test(working)) {
      working = working
        .replace(/\{\s*/g, "{\n")
        .replace(/;\s*/g, ";\n")
        .replace(/\s*\}/g, "\n}")
        .replace(/\n{2,}/g, "\n");
    }
    let indent = 0;
    const formattedLines = working.split("\n").map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return "";
      if (/^[}\])]/.test(trimmed)) {
        indent = Math.max(0, indent - 1);
      }
      const out = `${"  ".repeat(indent)}${trimmed}`;
      const opens = (trimmed.match(/[{[(]/g) || []).length;
      const closes = (trimmed.match(/[}\])]/g) || []).length;
      const net = opens - closes + (/^[}\])]/.test(trimmed) ? 1 : 0);
      indent = Math.max(0, indent + net);
      return out;
    });
    return { text: formattedLines.join("\n").trim() };
  }

  if (kind === "markdown") {
    const lines = normalizedEol
      .split("\n")
      .map((l) => l.replace(/\t/g, "  ").replace(/[ \t]+$/g, ""));
    return { text: lines.join("\n").replace(/\n{3,}/g, "\n\n").trim() };
  }

  return {
    text: normalizedEol
      .split("\n")
      .map((l) => l.replace(/[ \t]+$/g, ""))
      .join("\n")
      .trim(),
  };
}

interface DiffRow {
  type: "equal" | "modify" | "add" | "remove";
  leftLineNo: number | null;
  rightLineNo: number | null;
  leftText: string;
  rightText: string;
}

type RenderSegment =
  | { kind: "row"; row: DiffRow; rowIndex: number }
  | { kind: "collapsed"; id: string; hiddenCount: number; startIndex: number; endIndex: number };

function buildAlignedRows(leftSource: string, rightSource: string, ignoreWhitespace: boolean): {
  rows: DiffRow[];
  addedCount: number;
  removedCount: number;
  modifiedCount: number;
} {
  const prep = (s: string) =>
    ignoreWhitespace
      ? s
          .replace(/\r\n/g, "\n")
          .split("\n")
          .map((l) => l.trim().replace(/\s+/g, " "))
          .join("\n")
      : s.replace(/\r\n/g, "\n");

  const changes = diffLines(prep(leftSource), prep(rightSource));
  const origLeftLines = leftSource.replace(/\r\n/g, "\n").split("\n");
  const origRightLines = rightSource.replace(/\r\n/g, "\n").split("\n");

  let lCursor = 0;
  let rCursor = 0;
  const rows: DiffRow[] = [];
  let addedCount = 0;
  let removedCount = 0;
  let modifiedCount = 0;

  let i = 0;
  while (i < changes.length) {
    const part = changes[i];
    const count = part.count || 0;

    if (!part.added && !part.removed) {
      for (let k = 0; k < count; k++) {
        rows.push({
          type: "equal",
          leftLineNo: lCursor + 1,
          rightLineNo: rCursor + 1,
          leftText: origLeftLines[lCursor] ?? "",
          rightText: origRightLines[rCursor] ?? "",
        });
        lCursor++;
        rCursor++;
      }
      i++;
      continue;
    }

    if (part.removed && i + 1 < changes.length && changes[i + 1].added) {
      const nextAdded = changes[i + 1];
      const remCount = count;
      const addCount = nextAdded.count || 0;
      const maxLen = Math.max(remCount, addCount);

      for (let k = 0; k < maxLen; k++) {
        const hasL = k < remCount;
        const hasR = k < addCount;
        if (hasL && hasR) {
          modifiedCount++;
          rows.push({
            type: "modify",
            leftLineNo: lCursor + 1,
            rightLineNo: rCursor + 1,
            leftText: origLeftLines[lCursor] ?? "",
            rightText: origRightLines[rCursor] ?? "",
          });
          lCursor++;
          rCursor++;
        } else if (hasL) {
          removedCount++;
          rows.push({
            type: "remove",
            leftLineNo: lCursor + 1,
            rightLineNo: null,
            leftText: origLeftLines[lCursor] ?? "",
            rightText: "",
          });
          lCursor++;
        } else if (hasR) {
          addedCount++;
          rows.push({
            type: "add",
            leftLineNo: null,
            rightLineNo: rCursor + 1,
            leftText: "",
            rightText: origRightLines[rCursor] ?? "",
          });
          rCursor++;
        }
      }
      i += 2;
      continue;
    }

    if (part.removed) {
      for (let k = 0; k < count; k++) {
        removedCount++;
        rows.push({
          type: "remove",
          leftLineNo: lCursor + 1,
          rightLineNo: null,
          leftText: origLeftLines[lCursor] ?? "",
          rightText: "",
        });
        lCursor++;
      }
    } else if (part.added) {
      for (let k = 0; k < count; k++) {
        addedCount++;
        rows.push({
          type: "add",
          leftLineNo: null,
          rightLineNo: rCursor + 1,
          leftText: "",
          rightText: origRightLines[rCursor] ?? "",
        });
        rCursor++;
      }
    }
    i++;
  }

  return { rows, addedCount, removedCount, modifiedCount };
}

function buildFoldedSegments(
  rows: DiffRow[],
  foldUnchanged: boolean,
  expandedIds: Set<string>,
  contextRadius = 3
): RenderSegment[] {
  if (!foldUnchanged) {
    return rows.map((row, rowIndex) => ({ kind: "row", row, rowIndex }));
  }

  const keep = new Array<boolean>(rows.length).fill(false);
  rows.forEach((r, idx) => {
    if (r.type !== "equal") {
      const start = Math.max(0, idx - contextRadius);
      const end = Math.min(rows.length - 1, idx + contextRadius);
      for (let k = start; k <= end; k++) keep[k] = true;
    }
  });

  const segments: RenderSegment[] = [];
  let i = 0;
  while (i < rows.length) {
    if (keep[i]) {
      segments.push({ kind: "row", row: rows[i], rowIndex: i });
      i++;
      continue;
    }
    const start = i;
    while (i < rows.length && !keep[i]) i++;
    const end = i - 1;
    const hiddenCount = end - start + 1;
    const id = `fold-${start}-${end}`;

    if (hiddenCount <= 2 || expandedIds.has(id)) {
      for (let k = start; k <= end; k++) {
        segments.push({ kind: "row", row: rows[k], rowIndex: k });
      }
    } else {
      segments.push({ kind: "collapsed", id, hiddenCount, startIndex: start, endIndex: end });
    }
  }

  return segments;
}

function SyntaxLine({ text, kind }: { text: string; kind: DetectedKind }) {
  if (!text) return <span>&nbsp;</span>;
  if (kind === "text" || kind === "markdown") {
    return <span>{text}</span>;
  }
  const prismLang = normalizeLanguage(kind);
  const grammar = Prism.languages[prismLang];
  if (!grammar) return <span>{text}</span>;

  const tokens = Prism.tokenize(text, grammar);
  const renderToken = (tok: string | Prism.Token, idx: number): React.ReactNode => {
    if (typeof tok === "string") return tok;
    const colorClass =
      tok.type === "string" || tok.type === "attr-value"
        ? "text-emerald-700 dark:text-emerald-300"
        : tok.type === "keyword" || tok.type === "tag" || tok.type === "property"
          ? "text-sky-700 dark:text-sky-300"
          : tok.type === "number" || tok.type === "boolean"
            ? "text-amber-700 dark:text-amber-300"
            : tok.type === "comment"
              ? "text-neutral-400 italic"
              : "";
    const content = Array.isArray(tok.content)
      ? tok.content.map((c, ci) => renderToken(c, ci))
      : typeof tok.content === "string"
        ? tok.content
        : renderToken(tok.content, 0);
    return (
      <span key={idx} className={colorClass}>
        {content}
      </span>
    );
  };

  return <>{tokens.map((t, idx) => renderToken(t, idx))}</>;
}

function WordDiffSpan({
  left,
  right,
  side,
}: {
  left: string;
  right: string;
  side: "left" | "right";
}) {
  const parts = diffWordsWithSpace(left, right);
  return (
    <>
      {parts.map((p, idx) => {
        if (side === "left") {
          if (p.added) return null;
          if (p.removed) {
            return (
              <mark
                key={idx}
                className="rounded-xs bg-red-500/30 px-0.5 text-red-950 line-through decoration-red-600/60 dark:bg-red-500/35 dark:text-red-100"
              >
                {p.value}
              </mark>
            );
          }
          return <span key={idx}>{p.value}</span>;
        }
        if (p.removed) return null;
        if (p.added) {
          return (
            <mark
              key={idx}
              className="rounded-xs bg-emerald-500/30 px-0.5 font-medium text-emerald-950 dark:bg-emerald-500/35 dark:text-emerald-100"
            >
              {p.value}
            </mark>
          );
        }
        return <span key={idx}>{p.value}</span>;
      })}
    </>
  );
}

function renderInlineMarkdown(text: string): React.ReactNode[] {
  const regex = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[2]) {
      out.push(
        <strong key={k++} className="font-bold text-neutral-900 dark:text-white">
          {m[2]}
        </strong>
      );
    } else if (m[3]) {
      out.push(<em key={k++}>{m[3]}</em>);
    } else if (m[4]) {
      out.push(
        <code
          key={k++}
          className="rounded bg-neutral-200/80 px-1.5 py-0.5 font-mono text-xs text-pink-700 dark:bg-neutral-800 dark:text-pink-300"
        >
          {m[4]}
        </code>
      );
    } else if (m[5] && m[6]) {
      const safeHref = /^(https?:\/\/|\/|#)/i.test(m[6].trim()) ? m[6].trim() : "#";
      out.push(
        <a
          key={k++}
          href={safeHref}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-sky-600 underline underline-offset-2 dark:text-sky-400"
        >
          {m[5]}
        </a>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function MarkdownPreviewPane({ source }: { source: string }) {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i++;
      continue;
    }

    if (line.trim().startsWith("```")) {
      const lang = line.trim().slice(3).trim() || "typescript";
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      i++;
      elements.push(
        <div
          key={`code-${i}`}
          className="my-4 overflow-hidden rounded-xl border border-neutral-800 bg-zinc-950"
        >
          <div className="border-b border-neutral-800 px-3 py-1.5 font-mono text-[11px] uppercase text-neutral-400">
            {lang}
          </div>
          <div className="p-3">
            <CodeHighlight code={codeLines.join("\n")} language={lang} />
          </div>
        </div>
      );
      continue;
    }

    if (
      line.trim().startsWith("|") &&
      i + 1 < lines.length &&
      /^\|[\s:|-]+\|/.test(lines[i + 1].trim())
    ) {
      const parseCells = (row: string) =>
        row
          .trim()
          .replace(/^\||\|$/g, "")
          .split("|")
          .map((c) => c.trim());
      const headers = parseCells(line);
      i += 2;
      const bodyRows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        bodyRows.push(parseCells(lines[i]));
        i++;
      }
      elements.push(
        <div key={`tbl-${i}`} className="my-4 overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-800">
          <table className="w-full text-left text-xs sm:text-sm">
            <thead className="bg-neutral-100 dark:bg-neutral-800/80">
              <tr>
                {headers.map((h, hi) => (
                  <th key={hi} className="px-3.5 py-2.5 font-semibold text-neutral-900 dark:text-white">
                    {renderInlineMarkdown(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {bodyRows.map((r, ri) => (
                <tr key={ri}>
                  {r.map((cell, ci) => (
                    <td key={ci} className="px-3.5 py-2.5 text-neutral-700 dark:text-neutral-300">
                      {renderInlineMarkdown(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    const headingMatch = line.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const cls =
        level === 1
          ? "text-xl sm:text-2xl font-black mt-4 mb-2"
          : level === 2
            ? "text-lg sm:text-xl font-bold mt-4 mb-2"
            : "text-base font-bold mt-3 mb-1.5";
      elements.push(
        <div key={`h-${i}`} className={`font-heading text-neutral-900 dark:text-white ${cls}`}>
          {renderInlineMarkdown(headingMatch[2])}
        </div>
      );
      i++;
      continue;
    }

    if (/^[-*]\s+/.test(line.trim())) {
      const items: Array<{ checked: boolean | null; text: string }> = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        const rawItem = lines[i].trim().replace(/^[-*]\s+/, "");
        const taskMatch = rawItem.match(/^\[([ xX])\]\s+(.+)$/);
        if (taskMatch) {
          items.push({ checked: taskMatch[1].toLowerCase() === "x", text: taskMatch[2] });
        } else {
          items.push({ checked: null, text: rawItem });
        }
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="my-3 space-y-1.5 text-sm">
          {items.map((it, idx) => (
            <li key={idx} className="flex items-start gap-2.5 text-neutral-700 dark:text-neutral-300">
              {it.checked !== null ? (
                <span
                  className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] font-bold ${
                    it.checked
                      ? "border-emerald-500 bg-emerald-500 text-white"
                      : "border-neutral-400 dark:border-neutral-600"
                  }`}
                >
                  {it.checked ? "✓" : ""}
                </span>
              ) : (
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" />
              )}
              <span>{renderInlineMarkdown(it.text)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    if (line.trim().startsWith(">")) {
      elements.push(
        <blockquote
          key={`bq-${i}`}
          className="my-3 border-l-3 border-sky-500 pl-3.5 text-sm italic text-neutral-600 dark:text-neutral-400"
        >
          {renderInlineMarkdown(line.trim().replace(/^>\s?/, ""))}
        </blockquote>
      );
      i++;
      continue;
    }

    elements.push(
      <p key={`p-${i}`} className="my-2.5 text-sm sm:text-[15px] leading-relaxed text-neutral-700 dark:text-neutral-300">
        {renderInlineMarkdown(line)}
      </p>
    );
    i++;
  }

  return <div>{elements}</div>;
}

export default function DiffCheckerPlayground() {
  const [leftText, setLeftText] = useState(PRESETS[0].left);
  const [rightText, setRightText] = useState(PRESETS[0].right);
  const [leftFileName, setLeftFileName] = useState<string | null>(null);
  const [rightFileName, setRightFileName] = useState<string | null>(null);
  const [manualKind, setManualKind] = useState<DetectedKind | "auto">("auto");
  const [viewMode, setViewMode] = useState<ViewMode>("split");
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(false);
  const [sortJsonKeys, setSortJsonKeys] = useState(true);
  const [foldUnchanged, setFoldUnchanged] = useState(false);
  const [expandedFolds, setExpandedFolds] = useState<Set<string>>(new Set());
  const [showEditors, setShowEditors] = useState(true);
  const [formatNotice, setFormatNotice] = useState<string | null>(null);
  const [copiedPatch, setCopiedPatch] = useState(false);

  const detectedKind = useMemo(() => detectContentKind(leftText, rightText), [leftText, rightText]);
  const activeKind: DetectedKind = manualKind === "auto" ? detectedKind : manualKind;

  const diffSummary = useMemo(
    () => buildAlignedRows(leftText, rightText, ignoreWhitespace),
    [leftText, rightText, ignoreWhitespace]
  );

  const segments = useMemo(
    () => buildFoldedSegments(diffSummary.rows, foldUnchanged, expandedFolds, 3),
    [diffSummary.rows, foldUnchanged, expandedFolds]
  );

  const totalChangedLines =
    diffSummary.addedCount + diffSummary.removedCount + diffSummary.modifiedCount;

  const handleSmartFormat = () => {
    const resLeft = smartFormatText(leftText, activeKind, sortJsonKeys);
    const resRight = smartFormatText(rightText, activeKind, sortJsonKeys);
    if (resLeft.error || resRight.error) {
      setFormatNotice(resLeft.error || resRight.error || null);
      return;
    }
    setLeftText(resLeft.text);
    setRightText(resRight.text);
    setFormatNotice(
      activeKind === "json" && sortJsonKeys
        ? "JSON beidseitig eingerückt und Objektschlüssel rekursiv alphabetisch sortiert."
        : `${KIND_LABELS[activeKind]} beidseitig bereinigt und formatiert.`
    );
    setTimeout(() => setFormatNotice(null), 3200);
  };

  const handleSwap = () => {
    setLeftText(rightText);
    setRightText(leftText);
    setLeftFileName(rightFileName);
    setRightFileName(leftFileName);
  };

  const loadLocalFile = async (side: "left" | "right", file?: File | null) => {
    if (!file) return;
    const text = await file.text();
    if (side === "left") {
      setLeftText(text);
      setLeftFileName(file.name);
    } else {
      setRightText(text);
      setRightFileName(file.name);
    }
  };

  const handleCopyPatch = async () => {
    const patch = createTwoFilesPatch(
      leftFileName || "original",
      rightFileName || "geaendert",
      leftText,
      rightText,
      "",
      ""
    );
    await navigator.clipboard?.writeText(patch);
    setCopiedPatch(true);
    setTimeout(() => setCopiedPatch(false), 1800);
  };

  const handleDownloadPatch = () => {
    const patch = createTwoFilesPatch(
      leftFileName || "original",
      rightFileName || "geaendert",
      leftText,
      rightText,
      "",
      ""
    );
    const blob = new Blob([patch], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "aenderungen.patch";
    a.click();
    URL.revokeObjectURL(url);
  };

  const leftLineCount = leftText ? leftText.replace(/\r\n/g, "\n").split("\n").length : 0;
  const rightLineCount = rightText ? rightText.replace(/\r\n/g, "\n").split("\n").length : 0;

  return (
    <div className="flex flex-col gap-5">
      {/* 1. UNIFIED FULL-WIDTH STUDIO COMMAND BAR */}
      <div className="flex flex-col gap-3 rounded-2xl border border-neutral-200/90 bg-neutral-50/80 p-3.5 sm:p-4 dark:border-neutral-800 dark:bg-neutral-950/60">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Primary View Mode Tabs */}
          <div
            role="tablist"
            aria-label="Diff-Ansichten"
            className="inline-flex flex-wrap gap-1 rounded-xl border border-neutral-200 bg-white p-1 shadow-2xs dark:border-neutral-800 dark:bg-neutral-900"
          >
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "split"}
              onClick={() => setViewMode("split")}
              className={`inline-flex min-h-[38px] items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
                viewMode === "split"
                  ? "bg-sky-600 text-white shadow-2xs"
                  : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
              }`}
            >
              <Columns2 className="h-4 w-4" />
              <span>Split-Diff (Nebeneinander)</span>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "unified"}
              onClick={() => setViewMode("unified")}
              className={`inline-flex min-h-[38px] items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
                viewMode === "unified"
                  ? "bg-sky-600 text-white shadow-2xs"
                  : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
              }`}
            >
              <AlignJustify className="h-4 w-4" />
              <span>Unified-Diff</span>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "markdown"}
              onClick={() => setViewMode("markdown")}
              className={`inline-flex min-h-[38px] items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
                viewMode === "markdown"
                  ? "bg-sky-600 text-white shadow-2xs"
                  : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
              }`}
            >
              <BookOpen className="h-4 w-4" />
              <span>Markdown Live-Vorschau</span>
              {activeKind === "markdown" && viewMode !== "markdown" && (
                <span className="rounded-full bg-sky-500/20 px-1.5 py-0.5 text-[10px] font-bold text-sky-700 dark:text-sky-300">
                  Erkannt
                </span>
              )}
            </button>
          </div>

          {/* Language Selector + Smart Format + Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex min-h-[38px] items-center gap-1.5 rounded-xl border border-sky-500/30 bg-sky-500/10 px-3 py-1 text-xs">
              <FileCode2 className="h-4 w-4 text-sky-600 dark:text-sky-400" />
              <label htmlFor="diff-kind-select" className="sr-only">
                Sprachmodus wählen
              </label>
              <select
                id="diff-kind-select"
                value={manualKind}
                onChange={(e) => setManualKind(e.target.value as DetectedKind | "auto")}
                className="bg-transparent font-semibold text-sky-900 focus:outline-hidden dark:text-sky-200 cursor-pointer"
              >
                <option value="auto">Auto: {KIND_LABELS[detectedKind]}</option>
                <option value="json">JSON</option>
                <option value="markdown">Markdown</option>
                <option value="typescript">TypeScript / JS</option>
                <option value="css">CSS</option>
                <option value="markup">HTML / XML</option>
                <option value="text">Klartext / Vertrag</option>
              </select>
            </div>

            <button
              type="button"
              onClick={handleSmartFormat}
              className="inline-flex min-h-[38px] items-center gap-1.5 rounded-xl bg-sky-600 px-3.5 py-1.5 text-xs sm:text-sm font-bold text-white shadow-2xs transition-colors hover:bg-sky-700 cursor-pointer"
            >
              <Wand2 className="h-4 w-4" />
              <span>Smart Formatieren</span>
            </button>

            <button
              type="button"
              onClick={handleSwap}
              title="Links und Rechts tauschen"
              className="inline-flex min-h-[38px] items-center gap-1.5 rounded-xl border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 cursor-pointer"
            >
              <ArrowLeftRight className="h-3.5 w-3.5" />
              <span>Tauschen</span>
            </button>

            <button
              type="button"
              onClick={() => setShowEditors((s) => !s)}
              className={`inline-flex min-h-[38px] items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold cursor-pointer ${
                showEditors
                  ? "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200"
                  : "border-sky-500/40 bg-sky-500/15 text-sky-800 dark:text-sky-200"
              }`}
            >
              <PenLine className="h-3.5 w-3.5" />
              <span>{showEditors ? "Editor einklappen" : "Editor aufklappen"}</span>
            </button>
          </div>
        </div>

        {/* Secondary Row: Presets + Checkboxes + Diff Telemetry */}
        <div className="flex flex-wrap items-center justify-between gap-2.5 border-t border-neutral-200/70 pt-2.5 text-xs dark:border-neutral-800/70">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-neutral-500 dark:text-neutral-400 mr-1">
              Vorlage laden:
            </span>
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setLeftText(p.left);
                  setRightText(p.right);
                  setLeftFileName(null);
                  setRightFileName(null);
                  setManualKind("auto");
                  setExpandedFolds(new Set());
                  if (p.defaultView) setViewMode(p.defaultView);
                }}
                className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 cursor-pointer"
              >
                <Sparkles className="h-3 w-3 text-sky-500" />
                {p.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setLeftText("");
                setRightText("");
                setLeftFileName(null);
                setRightFileName(null);
                setShowEditors(true);
              }}
              className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-600 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400 cursor-pointer"
            >
              <Trash2 className="h-3 w-3" />
              Leeren
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3.5">
            {activeKind === "json" && (
              <label className="inline-flex cursor-pointer items-center gap-1.5 font-medium text-neutral-700 dark:text-neutral-300">
                <input
                  type="checkbox"
                  checked={sortJsonKeys}
                  onChange={(e) => setSortJsonKeys(e.target.checked)}
                  className="rounded border-neutral-300"
                />
                <span>JSON-Keys sortieren</span>
              </label>
            )}

            <label className="inline-flex cursor-pointer items-center gap-1.5 font-medium text-neutral-700 dark:text-neutral-300">
              <input
                type="checkbox"
                checked={ignoreWhitespace}
                onChange={(e) => setIgnoreWhitespace(e.target.checked)}
                className="rounded border-neutral-300"
              />
              <span>Whitespace ignorieren</span>
            </label>

            {viewMode !== "markdown" && (
              <label className="inline-flex cursor-pointer items-center gap-1.5 font-medium text-neutral-700 dark:text-neutral-300">
                <input
                  type="checkbox"
                  checked={foldUnchanged}
                  onChange={(e) => {
                    setFoldUnchanged(e.target.checked);
                    setExpandedFolds(new Set());
                  }}
                  className="rounded border-neutral-300"
                />
                <span>Unverändertes falten (±3 Z.)</span>
              </label>
            )}

            <div className="flex items-center gap-1.5 font-mono text-xs font-bold">
              <span className="rounded-md bg-emerald-500/15 px-2 py-0.5 text-emerald-700 dark:text-emerald-300">
                +{diffSummary.addedCount + diffSummary.modifiedCount}
              </span>
              <span className="rounded-md bg-red-500/15 px-2 py-0.5 text-red-700 dark:text-red-300">
                −{diffSummary.removedCount + diffSummary.modifiedCount}
              </span>
            </div>
          </div>
        </div>
      </div>

      {formatNotice && (
        <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 px-4 py-2.5 text-xs font-medium text-sky-900 dark:text-sky-200">
          {formatNotice}
        </div>
      )}

      {/* 2. COLLAPSIBLE SIDE-BY-SIDE INPUT WORKBENCH WITH DRAG & DROP FILE LOAD */}
      {showEditors && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Left Editor */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              void loadLocalFile("left", e.dataTransfer.files?.[0]);
            }}
            className="flex flex-col overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-2xs dark:border-neutral-800 dark:bg-neutral-950/70"
          >
            <div className="flex items-center justify-between border-b border-neutral-200/80 bg-neutral-50/90 px-4 py-2.5 text-xs dark:border-neutral-800 dark:bg-neutral-900/80">
              <div className="flex items-center gap-2 font-semibold text-neutral-800 dark:text-neutral-200">
                <span className="h-2 w-2 rounded-full bg-red-500/80" />
                <label htmlFor="diff-left-input">
                  {leftFileName ? `Original: ${leftFileName}` : "Original (Vorher)"}
                </label>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[11px] text-neutral-500">
                  {leftLineCount} Z. · {leftText.length} Zeichen
                </span>
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-neutral-200 bg-white px-2 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                  <Upload className="h-3 w-3" />
                  <span>Datei öffnen</span>
                  <input
                    type="file"
                    onChange={(e) => void loadLocalFile("left", e.target.files?.[0])}
                    className="sr-only"
                  />
                </label>
              </div>
            </div>
            <textarea
              id="diff-left-input"
              rows={9}
              value={leftText}
              onChange={(e) => setLeftText(e.target.value)}
              spellCheck={false}
              placeholder="Originaltext, JSON, Code oder Markdown einfügen (oder Datei hierher ziehen)..."
              className="w-full resize-y bg-transparent p-3.5 font-mono text-[13px] leading-relaxed text-neutral-900 focus:outline-hidden dark:text-neutral-100"
            />
          </div>

          {/* Right Editor */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              void loadLocalFile("right", e.dataTransfer.files?.[0]);
            }}
            className="flex flex-col overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-2xs dark:border-neutral-800 dark:bg-neutral-950/70"
          >
            <div className="flex items-center justify-between border-b border-neutral-200/80 bg-neutral-50/90 px-4 py-2.5 text-xs dark:border-neutral-800 dark:bg-neutral-900/80">
              <div className="flex items-center gap-2 font-semibold text-neutral-800 dark:text-neutral-200">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <label htmlFor="diff-right-input">
                  {rightFileName ? `Neu: ${rightFileName}` : "Neue Fassung (Nachher)"}
                </label>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[11px] text-neutral-500">
                  {rightLineCount} Z. · {rightText.length} Zeichen
                </span>
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-neutral-200 bg-white px-2 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                  <Upload className="h-3 w-3" />
                  <span>Datei öffnen</span>
                  <input
                    type="file"
                    onChange={(e) => void loadLocalFile("right", e.target.files?.[0])}
                    className="sr-only"
                  />
                </label>
              </div>
            </div>
            <textarea
              id="diff-right-input"
              rows={9}
              value={rightText}
              onChange={(e) => setRightText(e.target.value)}
              spellCheck={false}
              placeholder="Überarbeiteten Text, neues JSON, Code oder Markdown einfügen (oder Datei hierher ziehen)..."
              className="w-full resize-y bg-transparent p-3.5 font-mono text-[13px] leading-relaxed text-neutral-900 focus:outline-hidden dark:text-neutral-100"
            />
          </div>
        </div>
      )}

      {/* Markdown Quick-Banner when Markdown is detected */}
      {activeKind === "markdown" && viewMode !== "markdown" && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-sky-500/25 bg-sky-500/5 px-4 py-2.5 text-xs">
          <span className="text-neutral-700 dark:text-neutral-300">
            <strong>Markdown erkannt:</strong> Du kannst dir Tabellen, Überschriften und Checklisten auch direkt nebeneinander gerendert ansehen.
          </span>
          <button
            type="button"
            onClick={() => setViewMode("markdown")}
            className="inline-flex items-center gap-1.5 font-semibold text-sky-700 hover:underline dark:text-sky-300 cursor-pointer"
          >
            <BookOpen className="h-3.5 w-3.5" />
            <span>Gerenderte Markdown-Vorschau öffnen →</span>
          </button>
        </div>
      )}

      {/* 3. FULL-WIDTH DIFF / MARKDOWN OUTPUT STAGE */}
      <div className="overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-xs dark:border-neutral-800 dark:bg-neutral-950">
        {viewMode === "markdown" ? (
          <div className="grid grid-cols-1 divide-y divide-neutral-200 dark:divide-neutral-800 lg:grid-cols-2 lg:divide-x lg:divide-y-0">
            <div className="p-5 sm:p-7">
              <div className="mb-4 inline-flex rounded-md bg-neutral-100 px-2.5 py-1 font-mono text-xs font-semibold text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                Gerendert: Original (Vorher)
              </div>
              <MarkdownPreviewPane source={leftText} />
            </div>
            <div className="p-5 sm:p-7">
              <div className="mb-4 inline-flex rounded-md bg-emerald-500/15 px-2.5 py-1 font-mono text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                Gerendert: Neue Fassung (Nachher)
              </div>
              <MarkdownPreviewPane source={rightText} />
            </div>
          </div>
        ) : totalChangedLines === 0 && (leftText.trim() || rightText.trim()) ? (
          <div className="p-10 text-center text-sm font-semibold text-emerald-700 dark:text-emerald-400">
            ✓ Beide Seiten sind unter den aktiven Vergleichsregeln zu 100 % identisch.
          </div>
        ) : viewMode === "split" ? (
          <div className="max-h-[70vh] min-h-[340px] overflow-auto font-mono text-[13px] leading-relaxed">
            {/* Sticky Split Column Headers */}
            <div className="sticky top-0 z-10 grid grid-cols-2 border-b border-neutral-200 bg-neutral-100/95 text-xs font-semibold text-neutral-700 backdrop-blur-xs dark:border-neutral-800 dark:bg-neutral-900/95 dark:text-neutral-300">
              <div className="border-r border-neutral-200 px-4 py-2 dark:border-neutral-800">
                Original ({leftLineCount} Zeilen)
              </div>
              <div className="px-4 py-2">Neue Fassung ({rightLineCount} Zeilen)</div>
            </div>

            <table className="w-full border-collapse table-fixed">
              <colgroup>
                <col className="w-12" />
                <col className="w-[calc(50%-3rem)]" />
                <col className="w-12" />
                <col className="w-[calc(50%-3rem)]" />
              </colgroup>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-900">
                {segments.map((seg) => {
                  if (seg.kind === "collapsed") {
                    return (
                      <tr key={seg.id} className="bg-neutral-100/80 dark:bg-neutral-900/80">
                        <td colSpan={4} className="py-1.5 text-center">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedFolds((prev) => new Set(prev).add(seg.id))
                            }
                            className="inline-flex items-center gap-1.5 rounded-md px-3 py-0.5 font-sans text-xs font-semibold text-sky-700 hover:bg-sky-500/10 dark:text-sky-300 cursor-pointer"
                          >
                            <ChevronsUpDown className="h-3.5 w-3.5" />
                            <span>{seg.hiddenCount} unveränderte Zeilen einblenden</span>
                          </button>
                        </td>
                      </tr>
                    );
                  }

                  const { row, rowIndex } = seg;
                  const leftBg =
                    row.type === "remove" || row.type === "modify"
                      ? "bg-red-500/10 dark:bg-red-500/15"
                      : "";
                  const rightBg =
                    row.type === "add" || row.type === "modify"
                      ? "bg-emerald-500/10 dark:bg-emerald-500/15"
                      : "";

                  return (
                    <tr key={rowIndex} className="align-top">
                      <td className="select-none border-r border-neutral-200/70 bg-neutral-50 px-2.5 py-1 text-right text-[11px] text-neutral-400 dark:border-neutral-800 dark:bg-neutral-900/80">
                        {row.leftLineNo ?? ""}
                      </td>
                      <td className={`break-words whitespace-pre-wrap px-3.5 py-1 ${leftBg}`}>
                        {row.type === "modify" ? (
                          <WordDiffSpan left={row.leftText} right={row.rightText} side="left" />
                        ) : row.leftLineNo !== null ? (
                          <SyntaxLine text={row.leftText} kind={activeKind} />
                        ) : null}
                      </td>
                      <td className="select-none border-x border-neutral-200/70 bg-neutral-50 px-2.5 py-1 text-right text-[11px] text-neutral-400 dark:border-neutral-800 dark:bg-neutral-900/80">
                        {row.rightLineNo ?? ""}
                      </td>
                      <td className={`break-words whitespace-pre-wrap px-3.5 py-1 ${rightBg}`}>
                        {row.type === "modify" ? (
                          <WordDiffSpan left={row.leftText} right={row.rightText} side="right" />
                        ) : row.rightLineNo !== null ? (
                          <SyntaxLine text={row.rightText} kind={activeKind} />
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="max-h-[70vh] min-h-[340px] overflow-auto font-mono text-[13px] leading-relaxed">
            <table className="w-full border-collapse">
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-900">
                {segments.map((seg) => {
                  if (seg.kind === "collapsed") {
                    return (
                      <tr key={seg.id} className="bg-neutral-100/80 dark:bg-neutral-900/80">
                        <td colSpan={4} className="py-1.5 text-center">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedFolds((prev) => new Set(prev).add(seg.id))
                            }
                            className="inline-flex items-center gap-1.5 rounded-md px-3 py-0.5 font-sans text-xs font-semibold text-sky-700 hover:bg-sky-500/10 dark:text-sky-300 cursor-pointer"
                          >
                            <ChevronsUpDown className="h-3.5 w-3.5" />
                            <span>{seg.hiddenCount} unveränderte Zeilen einblenden</span>
                          </button>
                        </td>
                      </tr>
                    );
                  }

                  const { row, rowIndex } = seg;
                  if (row.type === "equal") {
                    return (
                      <tr key={rowIndex}>
                        <td className="w-12 select-none bg-neutral-50 px-2.5 py-1 text-right text-[11px] text-neutral-400 dark:bg-neutral-900">
                          {row.leftLineNo}
                        </td>
                        <td className="w-12 select-none border-r border-neutral-200/70 bg-neutral-50 px-2.5 py-1 text-right text-[11px] text-neutral-400 dark:border-neutral-800 dark:bg-neutral-900">
                          {row.rightLineNo}
                        </td>
                        <td className="w-7 select-none text-center text-neutral-400">&nbsp;</td>
                        <td className="whitespace-pre-wrap px-3 py-1">
                          <SyntaxLine text={row.leftText} kind={activeKind} />
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <React.Fragment key={rowIndex}>
                      {(row.type === "remove" || row.type === "modify") && (
                        <tr className="bg-red-500/10 dark:bg-red-500/15">
                          <td className="w-12 select-none px-2.5 py-1 text-right text-[11px] text-red-600/80 dark:text-red-400">
                            {row.leftLineNo}
                          </td>
                          <td className="w-12 select-none border-r border-neutral-200/70 px-2.5 py-1 text-right text-[11px] text-neutral-400 dark:border-neutral-800" />
                          <td className="w-7 select-none text-center font-bold text-red-600 dark:text-red-400">
                            −
                          </td>
                          <td className="whitespace-pre-wrap px-3 py-1">
                            {row.type === "modify" ? (
                              <WordDiffSpan left={row.leftText} right={row.rightText} side="left" />
                            ) : (
                              <SyntaxLine text={row.leftText} kind={activeKind} />
                            )}
                          </td>
                        </tr>
                      )}
                      {(row.type === "add" || row.type === "modify") && (
                        <tr className="bg-emerald-500/10 dark:bg-emerald-500/15">
                          <td className="w-12 select-none px-2.5 py-1 text-right text-[11px] text-neutral-400" />
                          <td className="w-12 select-none border-r border-neutral-200/70 px-2.5 py-1 text-right text-[11px] text-emerald-600/80 dark:border-neutral-800 dark:text-emerald-400">
                            {row.rightLineNo}
                          </td>
                          <td className="w-7 select-none text-center font-bold text-emerald-600 dark:text-emerald-400">
                            +
                          </td>
                          <td className="whitespace-pre-wrap px-3 py-1">
                            {row.type === "modify" ? (
                              <WordDiffSpan left={row.leftText} right={row.rightText} side="right" />
                            ) : (
                              <SyntaxLine text={row.rightText} kind={activeKind} />
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 4. EXPORT FOOTER */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-500 dark:text-neutral-400">
        <span>
          Myers O(ND) Zeilen- & Wort-Vergleich · Drag &amp; Drop für lokale Dateien · 0 Bytes Netzwerk-Transfer
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleCopyPatch}
            className="inline-flex min-h-[38px] items-center gap-1.5 rounded-xl border border-neutral-200 bg-white px-3 py-1.5 font-semibold text-neutral-700 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 cursor-pointer"
          >
            {copiedPatch ? (
              <>
                <Check className="h-3.5 w-3.5 text-emerald-500" />
                <span>Unified-Patch kopiert!</span>
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                <span>Als .patch kopieren</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleDownloadPatch}
            className="inline-flex min-h-[38px] items-center gap-1.5 rounded-xl border border-neutral-200 bg-white px-3 py-1.5 font-semibold text-neutral-700 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 cursor-pointer"
          >
            <Download className="h-3.5 w-3.5" />
            <span>.patch herunterladen</span>
          </button>
        </div>
      </div>
    </div>
  );
}
